import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { SessionService } from '../../../services/session/session.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';
import { AppSettings } from '../../../app.settings';
import { TEAM_ADMIN_MANAGED_FEATURE_FLAG_ENVIRONMENT_COMPARISON } from '../../../services/managed-feature-flags/managed-feature-flag-keys';
import {
  ManagedFeatureFlag,
  ManagedFeatureFlagApp,
  ManagedFeatureFlagSection,
  ManagedFeatureFlagsService,
} from '../../../services/managed-feature-flags/managed-feature-flags.service';

interface FlagSectionView {
  id: string;
  name: string;
  flags: ManagedFeatureFlag[];
  total: number;
}

const UNSECTIONED = 'unsectioned';

type ManagedFeatureFlagStatusFilter = 'all' | 'active' | 'inactive';
type ManagedFeatureFlagDefaultFilter = 'all' | 'enabled' | 'disabled';

@Component({
  selector: 'app-managed-feature-flags-list',
  templateUrl: './managed-feature-flags-list.component.html',
  styleUrls: ['./managed-feature-flags-list.component.scss', './managed-feature-flags-list.sections.scss'],
})
export class ManagedFeatureFlagsListComponent implements OnInit, OnDestroy {
  protected connectedBackend = AppSettings.getEndpoint();
  protected connectedEnvironment = AppSettings.getReleaseVersion();
  protected flags: ManagedFeatureFlag[] = [];
  protected filteredFlags: ManagedFeatureFlag[] = [];
  protected loading = false;
  protected loadFailed = false;
  protected comparisonEnabled = false;
  protected sections: ManagedFeatureFlagSection[] = [];
  protected groups: FlagSectionView[] = [];
  protected selectedIds = new Set<string>();
  protected moveTarget = UNSECTIONED;
  protected focusedSection = '';
  protected collapsed = new Set<string>();
  protected saving = false;
  protected organizationError = '';
  protected organizationMessage = '';
  protected editingSectionId: string | null = null;
  protected sectionName = '';
  private originalSectionName = '';
  private preferenceKey = '';
  private generation = 0;
  private destroyed = false;
  protected filters: {
    search: string;
    app: 'all' | ManagedFeatureFlagApp;
    status: ManagedFeatureFlagStatusFilter;
    defaultState: ManagedFeatureFlagDefaultFilter;
  } = {
    search: '',
    app: 'all',
    status: 'all',
    defaultState: 'all',
  };

  constructor(
    private readonly managedFeatureFlagsService: ManagedFeatureFlagsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly session: SessionService,
    private readonly dialog: MatDialog,
  ) {}

  async ngOnInit(): Promise<void> {
    this.hydrateFiltersFromUrl();
    const userId = this.session.getUserId();
    this.preferenceKey = userId ? `managed-flag-sections:${encodeURIComponent(this.connectedBackend)}:${encodeURIComponent(userId)}` : '';
    try {
      const stored: unknown = this.preferenceKey ? JSON.parse(localStorage.getItem(this.preferenceKey) || '[]') : [];
      if (Array.isArray(stored)) { this.collapsed = new Set(stored.filter((id) => typeof id === 'string')); }
    } catch { /* Browser storage may be unavailable; the view remains usable. */ }
    await Promise.all([this.loadFlags(), this.loadComparisonRolloutFlag()]);
  }

  ngOnDestroy(): void { this.destroyed = true; ++this.generation; }

  protected async loadFlags(): Promise<void> {
    const generation = ++this.generation;
    this.loading = true;
    this.loadFailed = false;
    try {
      const [flags, sections] = await Promise.all([
        this.managedFeatureFlagsService.list(), this.managedFeatureFlagsService.listSections(),
      ]);
      if (this.destroyed || generation !== this.generation) { return; }
      if (flags.some((flag) => flag.sectionId && !sections.some((section) => section.id === flag.sectionId))) {
        throw new Error('Section layout changed during loading. Refresh to load a consistent view.');
      }
      this.flags = flags;
      this.sections = sections;
      const removedFocus = !!this.focusedSection && this.focusedSection !== UNSECTIONED
        && !sections.some((section) => section.id === this.focusedSection);
      if (removedFocus) { this.focusedSection = ''; }
      if (!sections.some((section) => section.id === this.moveTarget)) { this.moveTarget = UNSECTIONED; }
      this.applyFilters(removedFocus);
    } catch {
      if (this.destroyed || generation !== this.generation) { return; }
      this.flags = [];
      this.sections = [];
      this.groups = [];
      this.filteredFlags = [];
      this.selectedIds.clear();
      this.loadFailed = true;
    } finally {
      if (!this.destroyed && generation === this.generation) { this.loading = false; }
    }
  }

  protected applyFilters(updateUrl = true): void {
    const search = this.filters.search.trim().toLowerCase();
    this.filteredFlags = this.flags.filter((flag) => {
      const matchesSearch = !search
        || flag.key.toLowerCase().includes(search)
        || flag.name.toLowerCase().includes(search)
        || (flag.description || '').toLowerCase().includes(search);
      const matchesApp = this.filters.app === 'all' || flag.appTargets.includes(this.filters.app);
      const matchesStatus = this.filters.status === 'all'
        || (this.filters.status === 'active' && flag.isActive)
        || (this.filters.status === 'inactive' && !flag.isActive);
      const matchesDefault = this.filters.defaultState === 'all'
        || (this.filters.defaultState === 'enabled' && flag.defaultEnabled)
        || (this.filters.defaultState === 'disabled' && !flag.defaultEnabled);

      const matchesSection = !this.focusedSection || (flag.sectionId || UNSECTIONED) === this.focusedSection;
      return matchesSearch && matchesApp && matchesStatus && matchesDefault && matchesSection;
    });

    const definitions = [...this.sections, { id: UNSECTIONED, name: 'Unsectioned', position: 0 }];
    const filtering = !!search || this.filters.app !== 'all' || this.filters.status !== 'all' || this.filters.defaultState !== 'all';
    this.groups = definitions.map((section) => ({
      id: section.id, name: section.name,
      flags: this.filteredFlags.filter((flag) => (flag.sectionId || UNSECTIONED) === section.id),
      total: this.flags.filter((flag) => (flag.sectionId || UNSECTIONED) === section.id).length,
    })).filter((section) => (!this.focusedSection || section.id === this.focusedSection)
      && (!filtering || section.flags.length > 0));
    this.selectedIds = new Set([...this.selectedIds].filter((id) => this.filteredFlags.some((flag) => flag.id === id)));

    if (updateUrl) {
      void this.router.navigate([], {
        relativeTo: this.route,
        replaceUrl: true,
        queryParams: {
          section: this.focusedSection || null,
          search: this.filters.search.trim() || null,
          app: this.filters.app === 'all' ? null : this.filters.app,
          status: this.filters.status === 'all' ? null : this.filters.status,
          default: this.filters.defaultState === 'all' ? null : this.filters.defaultState,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  protected clearFilters(): void {
    this.focusedSection = '';
    this.filters = {
      search: '',
      app: 'all',
      status: 'all',
      defaultState: 'all',
    };
    this.applyFilters();
  }

  protected openFlag(flag: ManagedFeatureFlag): void {
    void this.router.navigate([flag.id], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  protected createFlag(): void {
    void this.router.navigate(['new'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  protected importFlag(): void {
    void this.router.navigate(['new'], {
      relativeTo: this.route,
      queryParams: { transfer: 'import' },
      queryParamsHandling: 'merge',
    });
  }

  protected compareEnvironments(): void {
    void this.router.navigate(['compare'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  private async loadComparisonRolloutFlag(): Promise<void> {
    this.comparisonEnabled = false;
    try {
      const enabled = await this.managedFeatureFlagsService.isEnabled(
        TEAM_ADMIN_MANAGED_FEATURE_FLAG_ENVIRONMENT_COMPARISON,
        'team-admin',
      );
      if (!this.destroyed) { this.comparisonEnabled = enabled; }
    } catch {
      this.comparisonEnabled = false;
    }
  }

  protected isExpanded(group: FlagSectionView): boolean {
    return !!this.filters.search.trim() || !this.collapsed.has(group.id);
  }

  protected toggleSection(id: string): void {
    if (this.collapsed.has(id)) { this.collapsed.delete(id); } else { this.collapsed.add(id); }
    this.savePreferences();
  }

  protected expandAll(expanded: boolean): void {
    for (const group of this.groups) {
      if (expanded) { this.collapsed.delete(group.id); } else { this.collapsed.add(group.id); }
    }
    this.savePreferences();
  }

  private savePreferences(): void {
    try {
      if (this.preferenceKey) { localStorage.setItem(this.preferenceKey, JSON.stringify([...this.collapsed])); }
    } catch { /* Preferences are optional when browser storage is blocked. */ }
  }

  protected focusSection(id: string): void {
    this.focusedSection = id;
    if (id) { this.collapsed.delete(id); this.savePreferences(); }
    this.applyFilters();
  }

  protected selectFlag(id: string, selected: boolean): void {
    if (selected) { this.selectedIds.add(id); } else { this.selectedIds.delete(id); }
  }

  protected selectGroup(group: FlagSectionView): void {
    for (const flag of group.flags) { this.selectedIds.add(flag.id); }
  }

  protected editSection(id = 'new'): void {
    this.editingSectionId = id;
    this.sectionName = this.sections.find((section) => section.id === id)?.name || '';
    // Keep the draft's precondition stable even when another operation reloads the list.
    this.originalSectionName = this.sectionName;
    this.organizationError = '';
  }

  protected async saveSection(): Promise<void> {
    const name = this.sectionName.trim();
    if (!name || this.editingSectionId === null) { return; }
    const section = this.sections.find((item) => item.id === this.editingSectionId);
    if (this.editingSectionId !== 'new' && !section) {
      this.organizationError = 'Section no longer exists. Refresh and try again.';
      return;
    }
    const saved = await this.mutateOrganization(() => section
      ? this.managedFeatureFlagsService.renameSection({ ...section, name: this.originalSectionName }, name)
      : this.managedFeatureFlagsService.createSection(name), section ? 'Section renamed.' : 'Section created.');
    if (saved) { this.editingSectionId = null; this.sectionName = ''; }
  }

  protected async moveSelected(): Promise<void> {
    if (!this.selectedIds.size) { return; }
    const ids = [...this.selectedIds];
    const saved = await this.mutateOrganization(() => this.managedFeatureFlagsService.assignSection(
      ids, this.moveTarget === UNSECTIONED ? null : this.moveTarget,
    ), `${ids.length} ${ids.length === 1 ? 'flag moved' : 'flags moved'}.`);
    if (saved) { this.selectedIds.clear(); }
  }

  protected async moveSection(id: string, direction: number): Promise<void> {
    const previousIds = this.sections.map((section) => section.id);
    const ids = [...previousIds];
    const index = ids.indexOf(id);
    const next = index + direction;
    if (index < 0 || next < 0 || next >= ids.length) { return; }
    [ids[index], ids[next]] = [ids[next], ids[index]];
    await this.mutateOrganization(() => this.managedFeatureFlagsService.reorderSections(ids, previousIds), 'Section order saved.');
  }

  protected async deleteSection(group: FlagSectionView): Promise<void> {
    if (this.saving || this.loading) { return; }
    const confirmed = await this.dialog.open(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(`Delete section “${group.name}”`, `${group.total} ${group.total === 1 ? 'flag will' : 'flags will'} move to Unsectioned. No flags will be deleted.`),
    }).afterClosed().toPromise();
    if (!confirmed || this.destroyed) { return; }
    await this.mutateOrganization(() => this.managedFeatureFlagsService.deleteSection(group.id), 'Section deleted. Flags moved to Unsectioned.');
  }

  private async mutateOrganization(action: () => Promise<void>, message: string): Promise<boolean> {
    if (this.saving || this.loading || this.loadFailed || this.destroyed) { return false; }
    this.saving = true;
    this.organizationError = '';
    this.organizationMessage = '';
    try {
      await action();
      if (this.destroyed) { return false; }
      this.organizationMessage = message;
      await this.loadFlags();
      return true;
    } catch (error) {
      if (!this.destroyed) {
        const apiMessage = (error as { data?: { message?: unknown } })?.data?.message;
        this.organizationError = typeof apiMessage === 'string' ? apiMessage : 'Unable to save section changes. Refresh and try again.';
      }
      return false;
    } finally { this.saving = false; }
  }

  protected getOverrideCount(flag: ManagedFeatureFlag): number {
    return (flag.userOverrides?.includeUserIds?.length || 0)
      + (flag.userOverrides?.excludeUserIds?.length || 0);
  }

  protected trackByFlagId(_: number, flag: ManagedFeatureFlag): string {
    return flag.id;
  }

  private hydrateFiltersFromUrl(): void {
    const params = this.route.snapshot.queryParamMap;
    const app = params.get('app');
    const status = params.get('status');
    const defaultState = params.get('default');

    this.focusedSection = params.get('section') || '';
    this.filters.search = params.get('search') || '';
    this.filters.app = app === 'team-admin' || app === 'customer-app' || app === 'backend' ? app : 'all';
    this.filters.status = status === 'active' || status === 'inactive' ? status : 'all';
    this.filters.defaultState = defaultState === 'enabled' || defaultState === 'disabled'
      ? defaultState
      : 'all';
  }
}
